import {
    alpha,
    createTheme,
    type CSSVariablesResolver,
    defaultVariantColorsResolver,
    Loader,
    rem,
    rgba,
} from "@mantine/core";
import { shadows } from "./constants/shadows";
import { spacing } from "./constants/spacing";
import { blueColorsTuple, darkColors, darkColorsTuple, grayColorsTuple, lightColors } from "./constants/colors";
import { LoaderIcon } from "./lib/loader-icon";

const badgeOverrides = {
    "--badge-fz-xs": "var(--mantine-font-size-xxs)",
    "--badge-fz-sm": "var(--mantine-font-size-xs)",
    "--badge-fz-md": "var(--mantine-font-size-sm)",
    "--badge-fz-lg": "var(--mantine-font-size-sm)",
    "--badge-fz-xl": "var(--mantine-font-size-sm)",
    "--badge-height-xs": rem(16),
    "--badge-height-sm": rem(24),
    "--badge-height-md": rem(34),
    "--badge-height-lg": rem(34),
    "--badge-height-xl": rem(34),
    "--badge-padding-x-xs": "var(--mantine-spacing-sm)",
    "--badge-padding-x-sm": "var(--mantine-spacing-sm)",
    "--badge-padding-x-md": "var(--mantine-spacing-md)",
    "--badge-padding-x-lg": "var(--mantine-spacing-md)",
    "--badge-padding-x-xl": "var(--mantine-spacing-md)",
};

const avatarOverrides = {
    "--avatar-size-xs": "var(--ai-size-xs)",
    "--avatar-size-sm": "var(--ai-size-sm)",
    "--avatar-size-md": "var(--ai-size-md)",
    "--avatar-size-lg": "var(--ai-size-lg)",
    "--avatar-size-xl": "var(--ai-size-xl)",
    "--avatar-size-xxl": "var(--ai-size-xl)",
    "--avatar-size-input-xs": "var(--ai-size-input-xs)",
    "--avatar-size-input-sm": "var(--ai-size-input-sm)",
    "--avatar-size-input-md": "var(--ai-size-input-md)",
    "--avatar-size-input-lg": "var(--ai-size-input-lg)",
    "--avatar-size-input-xl": "var(--ai-size-input-xl)",
};

const segmentedControlOverrides = {
    "--sc-root-padding": rem(2),
    "--sc-color": "var(--surface-default-color)",
    "--sc-radius": "var(--mantine-radius-sm2)",
    "--sc-outer-radius": "calc(var(--sc-radius) + var(--sc-root-padding))",
    "--sc-padding-xs": rem(9),
    "--sc-padding-sm": rem(11),
    "--sc-padding-md": rem(11),
    "--sc-padding-lg": rem(11),
    "--sc-padding-xl": rem(11),
};

export const buttonOverrides = {
    "--button-height-xs": rem(30),
    "--button-height-sm": rem(40),
    "--button-height-md": rem(40),
    "--button-height-lg": rem(40),
    "--button-height-xl": rem(40),
    "--button-height-compact-xs": rem(22),
    "--button-height-compact-sm": rem(26),
    "--button-height-compact-md": rem(30),
    "--button-height-compact-lg": rem(30),
    "--button-height-compact-xl": rem(30),
    "--button-padding-x-xs": "var(--mantine-spacing-md)",
    "--button-padding-x-sm": "var(--mantine-spacing-lg)",
    "--button-padding-x-md": "var(--mantine-spacing-lg)",
    "--button-padding-x-lg": "var(--mantine-spacing-lg)",
    "--button-padding-x-xl": "var(--mantine-spacing-lg)",
    "--button-padding-x-compact-xs": "var(--mantine-spacing-sm)",
    "--button-padding-x-compact-sm": "var(--mantine-spacing-sm)",
    "--button-padding-x-compact-md": "var(--mantine-spacing-sm)",
    "--button-padding-x-compact-lg": "var(--mantine-spacing-sm)",
    "--button-padding-x-compact-xl": "var(--mantine-spacing-sm)",
    "--button-height-input-xs": "var(--ai-size-input-xs)",
    "--button-height-input-sm": "var(--ai-size-input-sm)",
    "--button-height-input-md": "var(--ai-size-input-md)",
    "--button-height-input-lg": "var(--ai-size-input-lg)",
    "--button-height-input-xl": "var(--ai-size-input-xl)",
    "--button-size": "var(--input-height)",
};

const inputOverrides = {
    "--input-height-xs": rem(34),
    "--input-height-sm": rem(40),
    "--input-height-md": rem(40),
    "--input-height-lg": rem(40),
    "--input-height-xl": rem(40),
    "--input-padding-y-xs": rem(4),
    "--input-padding-y-sm": rem(4),
    "--input-padding-y-md": rem(4),
    "--input-padding-y-lg": rem(4),
    "--input-padding-y-xl": rem(4),
    "--input-height": "var(--input-height-sm)",
    "--input-size": "var(--input-height)",
};

const actionIconOverrides = {
    "--ai-size-xxs": rem(16),
    "--ai-size-xs": rem(22),
    "--ai-size-sm": rem(26),
    "--ai-size-md": rem(32),
    "--ai-size-lg": rem(38),
    "--ai-size-xl": rem(44),
    "--ai-size-input-xs": rem(36),
    "--ai-size-input-sm": rem(40),
    "--ai-size-input-md": rem(42),
    "--ai-size-input-lg": rem(50),
    "--ai-size-input-xl": rem(60),
};

export const cssVariablesResolverFunction: CSSVariablesResolver = (theme) => ({
    variables: {
        "--textarea-vertical-padding": theme.spacing.md,
        "--accordion-icon-size": rem(36),
        "--scrollarea-scrollbar-default-size": theme.spacing.md,
        "--popover-radius": theme.radius.md,
        "--menu-item-color": "var(--mantine-color-title-2)",
        "--gray-light-hover": `${rgba(theme.colors.gray[6], 0.15)}`,
        "--mantine-color-indigo-cyan": `linear-gradient(90deg, #4871EB 0%, #009DC6 100%)`,
        "--mantine-color-lime-green": `linear-gradient(90deg, #00BB7D 0%, #68C937 100%)`,
        "--mantine-color-teal-blue": `linear-gradient(90deg, #00B194 0%, #1C8FD4 100%)`,
        "--mantine-color-blue-teal": `linear-gradient(90deg, #228BE6 0%, #19B999 20%)`,
        "--mantine-color-blue-teal-vertical": `linear-gradient(180deg, #228BE6 0%, #19B999 100%)`,
        "--mantine-color-orange-red": `linear-gradient(90deg, #FF7A30 0%, #FF5E4E 100%)`,
        "--mantine-color-peach": `linear-gradient(90deg, #F27794 0%, #F08779 100%)`,
        "--mantine-color-overlay-transparent": `linear-gradient(180deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 50%)`,
        // Text
        "--mantine-color-text-permanent-1": theme.white,
        "--mantine-color-text-permanent-2": theme.black,
        // Icon sizes
        "--icon-size-xxs": rem(12),
        "--icon-size-xs": rem(14),
        "--icon-size-sm": rem(16),
        "--icon-size-md": rem(20),
        "--icon-size-lg": rem(26),
        "--icon-size-xl": rem(30),
        // ActionIcon sizes
        ...actionIconOverrides,
        // Paper
        "--paper-radius": theme.radius.lg,
        "--paper-shadow": theme.shadows.xs,
        "--paper-background-color": "var(--surface-container-color)",
        "--paper-border-color": "var(--border-highlight-default-color)",
        // Header
        "--header-background-color": alpha("var(--mantine-color-body)", 0.8),
        // Input
        "--input-label-size": theme.fontSizes.xs,
        "--input-section-left-color": "var(--mantine-color-dimmed)",
        // Chart
        "--chart-other-color": "var(--mantine-color-neutral-4)",
        "--chart-accent-color": "var(--surface-primary-solid-color)",
        // Font Weights
        "--font-weight-regular": "400",
        "--font-weight-medium": "500",
        "--font-weight-semibold": "600",
        "--font-weight-bold": "700",
        // Customer Overview Container Heights
        "--customer-overview-donut-height": rem(272),
        "--customer-overview-tables-and-charts-height": rem(848),
        "--customer-overview-interactions-table-height": rem(768),
    },

    light: lightColors(theme),

    dark: darkColors(theme),
});

export const theme = createTheme({
    radius: {
        xs: rem(2),
        sm: rem(4),
        sm2: rem(6),
        md: rem(8),
        md2: rem(12),
        lg: rem(16),
        xl: rem(24),
        xxl: rem(32),
    },
    lineHeights: {
        xxs: "1.16",
        xs: "1.24",
        sm: "1.32",
        md: "1.40",
        lg: "1.48",
        xl: "1.56",
    },
    fontSizes: {
        xxs: rem(10),
        xs: rem(12),
        sm: rem(14),
        md: rem(16),
        lg: rem(18),
        xl: rem(20),
    },
    shadows,
    defaultRadius: "md",
    spacing,
    colors: {
        dark: darkColorsTuple,
        gray: grayColorsTuple,
        blue: blueColorsTuple,
    },
    components: {
        Badge: {
            vars: () => {
                return {
                    root: {
                        ...badgeOverrides,
                    },
                };
            },
        },
        Avatar: {
            vars: () => {
                return {
                    root: {
                        ...avatarOverrides,
                    },
                };
            },
        },
        SegmentedControl: {
            vars: () => {
                return {
                    root: {
                        ...segmentedControlOverrides,
                    },
                };
            },
        },
        Button: {
            vars: () => {
                return {
                    root: {
                        ...buttonOverrides,
                    },
                };
            },
        },
        Input: {
            vars: () => {
                return {
                    input: {
                        ...inputOverrides,
                    },
                };
            },
        },
        ActionIcon: {
            vars: () => {
                return {
                    root: {
                        ...actionIconOverrides,
                    },
                };
            },
        },
        Pill: {
            vars: () => {
                return {
                    root: {
                        "--pill-background-color": "var(--mantine-color-gray-1)",
                    },
                };
            },
        },
        Slider: {
            vars: () => {
                return {
                    root: {
                        "--slider-color": "var(--mantine-color-neutral-9)",
                    },
                };
            },
        },
        Loader: Loader.extend({
            defaultProps: {
                loaders: { ...Loader.defaultLoaders, oval: LoaderIcon },
            },
        }),
    },
    variantColorResolver: (input) => {
        const defaultResolvedColors = defaultVariantColorsResolver(input);
        const customColors = ["primary", "success", "warning", "error"];

        if (input.color === "red" && input.variant === "subtle") {
            return {
                ...defaultResolvedColors,
                color: "var(--mantine-color-red-6)",
            };
        }
        if (input.color === "dark" && input.variant === "subtle") {
            return {
                ...defaultResolvedColors,
                color: "var(--mantine-color-title-2)",
                // Background intentionally not set to keep the dark subtle variant using the default surface behavior.
            };
        }
        if (input.color === "dark" && input.variant === "light") {
            return {
                ...defaultResolvedColors,
                color: "var(--mantine-color-title-2)",
                // Background intentionally not set to keep the dark subtle variant using the default surface behavior.
            };
        }
        if (input.variant === "default") {
            return {
                ...defaultResolvedColors,
                border: `${rem(1)} solid var(--border-default-color)`,
                background: "var(--surface-default-color)",
                hover: "var(--surface-subtle-hover)",
            };
        }
        if (input.variant === "white") {
            return {
                ...defaultResolvedColors,
                border: `${rem(1)} solid var(--border-default-color)`,
                background: "var(--surface-default-color)",
                hover: "var(--surface-subtle-hover)",
            };
        }
        if (input.variant === "filled" && input.color === "green") {
            return {
                ...defaultResolvedColors,
                background: "var(--mantine-color-success-filled)",
                hover: "var(--mantine-color-success-filled-hover)",
            };
        }
        if (customColors.includes(input.color as string)) {
            switch (input.variant) {
                case "light":
                    return {
                        ...defaultResolvedColors,
                        background: `var(--surface-${input.color}-transparent, var(--surface-${input.color}-transparent-color))`,
                        color: `var(--mantine-color-text-${input.color}-prominent)`,
                    };
                case "filled":
                    return {
                        ...defaultResolvedColors,
                        background: `var(--surface-${input.color}-solid, var(--surface-${input.color}-solid-color))`,
                    };
                case "outline": {
                    return {
                        ...defaultResolvedColors,
                        color: `var(--mantine-color-text-${input.color}-default)`,
                        border: `${rem(1)} solid var(--mantine-color-border-${input.color}-default)`,
                    };
                }
            }
        }

        return defaultResolvedColors;
    },
});
